import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import Styles from './ChooseTemplate.module.scss'
import { useEffect, useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { Button } from 'blaze-ui/Button'
import { FilePathFormikField } from 'components/Wizards/FilePathFormikField/FilePathFormikField'
import { VisualElement, VisualElementsClient } from 'api/clients/VisualElementClient'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { Selection } from '@react-types/shared'
import { compact, sum, uniqBy } from 'lodash'
import { collectResults } from 'utils/query'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { Skeleton } from 'antd'
import { TemplatePreview } from 'components/Wizards/DesignMarketplaceWizard/SelectTemplate/TemplatePreview'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { Values } from '../../Types'
import * as CSS from 'csstype'
import { ArrowSkip } from 'blaze-ui/icons'
import { IFilters } from 'components/Templates/TemplatesModalV2/Types'
import { Filters } from 'components/Templates/TemplatesModalV2/components/Filters'
import { EmptyState } from 'components/Wizards/Wizard/Form/TemplateSelect/EmptyState'
import { useVisualElementsCounts } from 'hooks/queries/visualElements/useVisualElementsCounts'
import { TemplatesModalV2 } from 'components/Templates/TemplatesModalV2/TemplatesModalV2'
import { useModals } from 'almanac-ui/Modal'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { reportError } from 'utils/error'

const ITEMS_TO_LOAD = 4

interface Props extends StepProps {
  selectedMediaFile: IMediaFile
  createDesign: (values: Values) => Promise<void>
  setConfirmedVideo: (value: boolean) => void
}

export const ChooseTemplate = multiStep<Props>((props) => {
  const { schema, onSubmit, initialValues } = props
  const wizardSchema = useWizardSchema() as MultiStepSchema
  const [fieldsCompleted, setFieldsCompleted] = useState(false)
  const { mutate, data } = useCreateFieldsetCompletion()

  useEffect(() => {
    if (fieldsCompleted || !initialValues.topic) return

    mutate(
      {
        fields: ['filename'],
        completedFields: {
          topic: initialValues.topic,
        },
        generationType: wizardSchema.meta.channel,
      },
      {
        onError: (error) => {
          reportError(error)
        },
      },
    )
  }, [fieldsCompleted, initialValues.topic]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...props.initialValues }, { assert: false })}
    >
      {({ setFieldValue }) => (
        <>
          <PollFieldsetCompletion
            fieldsetCompletionId={data?.fieldset_completion.id}
            onComplete={(fieldset) => {
              setFieldsCompleted(true)

              Object.entries(fieldset.completed_fields).forEach(([key, value]) => {
                setFieldValue(key, value, true)
              })
            }}
          />
          <Content fieldsCompleted={fieldsCompleted} {...props} />
        </>
      )}
    </Formik>
  )
})

interface ContentProps extends Props {
  fieldsCompleted: boolean
}

const Content = multiStep<ContentProps>((props) => {
  const { schema, selectedMediaFile, createDesign, setConfirmedVideo, initialValues, onBack, fieldsCompleted } = props
  const { isSubmitting, setFieldValue, submitForm, values } = useFormikContext<Values>()
  const wizardSchema = useWizardSchema() as MultiStepSchema
  const { width, height } = initialValues
  const aspectRatio = width / height

  let itemWidth = 156
  let itemHeight = 156

  if (aspectRatio < 1) {
    itemHeight = 269
    itemWidth = 152
  } else if (aspectRatio > 1) {
    itemWidth = 145
    itemHeight = 108
  }

  const style: CSS.Properties = {
    ['--preview-width' as any]: `${itemWidth}px`,
    ['--preview-height' as any]: `${itemHeight}px`,
  }

  const [selectedTemplate, setSelectedTemplate] = useState<VisualElement>()
  const [filters, setFilters] = useState<IFilters>({
    styles: 'all' as Selection,
    categories: 'all' as Selection,
    types: new Set(['video']),
  })
  const [templateFromModal, setTemplateFromModal] = useState<VisualElement>()
  const { openModal, closeStack } = useModals()
  const queryFilters = {
    element_type: 'Template' as 'Template',
    type: filters.types === 'all' ? undefined : ([...filters.types] as string[]),
    style: filters.styles === 'all' ? undefined : ([...filters.styles] as string[]),
    category: filters.categories === 'all' ? undefined : ([...filters.categories] as string[]),
    ...filters,
    width,
    height,
  }

  const query = useVisualTemplates(
    {
      items: ITEMS_TO_LOAD,
      ...queryFilters,
      order_by: 'random',
    },
    {
      staleTime: Infinity,
    },
  )
  const templates = collectResults(query, (data) => data.visual_elements)
  const templatesToDisplay = compact(compact(uniqBy([templateFromModal].concat(templates), 'id'))).slice(0, 4)

  const { data, isLoading: isLoadingCount } = useVisualElementsCounts(
    { ...queryFilters, width, height },
    {
      staleTime: Infinity,
    },
  )
  const totalCount = data ? sum(Object.values(data)) : 0

  const handleSelectTemplate = (template?: VisualElement) => {
    setSelectedTemplate(template)
    setFieldValue('source_visual_element_id', template?.id)
  }

  const handleGoToDesigner = async () => {
    if (!values.initial_media_file_id) return

    await createDesign({
      ...initialValues,
      source_visual_element_id: selectedTemplate?.id,
      initial_media_file_id: values.initial_media_file_id,
      filename: values.filename || 'Video Post',
    })
  }

  const handleBack = () => {
    onBack?.()
    setConfirmedVideo(false)
  }

  const handleSeeAll = () => {
    openModal(
      TemplatesModalV2,
      {
        width,
        height,
        designChannel: wizardSchema.meta.channel as DesignChannel,
        disabledFilters: ['format', 'types'],
        onUse: async (templateId: number) => {
          const { data: template } = await VisualElementsClient.getElement(templateId)
          setTemplateFromModal(template)
          handleSelectTemplate(template)
          closeStack()
        },
        ...filters,
      },
      { newStack: true },
    )
  }

  const isLoading = query.isLoading

  return (
    <Form.Root>
      <Form.Section>
        <div className={Styles.container} style={style}>
          <div className={Styles.headerRow}>
            <Heading level={4}>Video Templates</Heading>
            <Filters
              value={filters}
              onChange={setFilters}
              hiddenFilters={['format', 'types']}
              disabledFilters={['format', 'types']}
              filterButtonVariant="secondary"
            />
          </div>
          {!isLoading && templatesToDisplay.length === 0 && <EmptyState />}
          <div className={Styles.templatesRowContainer}>
            <div className={Styles.templatesRow}>
              {isLoading && (
                <>
                  {Array.from({ length: ITEMS_TO_LOAD }).map((_, i) => (
                    <div className={Styles.templatePreviewContainer} key={`loader-${i}`}>
                      <ShimmeringColor aspectRatio={width / height} width={itemWidth} height={itemHeight} key={i} />
                      <Skeleton className={Styles.thinLine} active paragraph={{ rows: 1 }} title={false} />
                    </div>
                  ))}
                </>
              )}
              {!isLoading && (
                <>
                  {templatesToDisplay.map((template, i) => (
                    <TemplatePreview
                      key={`${template.id}-${i}`}
                      removeBackground={false}
                      template={template}
                      image={selectedMediaFile as IMediaFile}
                      onPress={handleSelectTemplate}
                      isSelected={selectedTemplate?.id === template.id}
                      isDisabled={isSubmitting}
                      withLabel
                    />
                  ))}
                  {!isLoadingCount && totalCount > ITEMS_TO_LOAD && (
                    <Button variant="secondary" size="sm" isDisabled={isSubmitting} onPress={handleSeeAll}>
                      See all {totalCount} templates
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          <BaseField label="Name your post and select a location" isRequired>
            <FilePathFormikField
              placeholder="Name your design..."
              locationPickerFullWidth
              inputClassName={Styles.filenameInput}
              isNameLoading={!fieldsCompleted}
            />
          </BaseField>
        </div>
      </Form.Section>

      <Form.Actions isSticky>
        <Form.PreviousButton onPress={handleBack}>Back</Form.PreviousButton>
        <Form.PrimaryActions>
          <Button
            frontIcon={ArrowSkip}
            variant="tertiary"
            size="lg"
            isDisabled={isSubmitting}
            onPress={handleGoToDesigner}
          >
            Go to Designer
          </Button>
          <Button variant="primary" size="lg" onPress={submitForm} isDisabled={isSubmitting || !selectedTemplate}>
            {schema.describe().meta.submitLabel}
          </Button>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
})
